import * as React from 'react'

import { BarChart, Box } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import DraggableNode from 'modules/core/components/DraggableNode'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'
import i18n from 'modules/core/lib/i18n'

import ChartTooltip from 'modules/insights/components/ChartTooltip'
import ChartTooltipPercentage from 'modules/insights/components/ChartTooltipPercentage'
import TooltipLegend from 'modules/insights/components/TooltipLegend'
import useMarginEffect from 'modules/insights/hooks/useMarginEffect'
import magnitude from 'modules/insights/lib/magnitude'
import type EmployeesPerLegalEntity from 'modules/insights/lib/metrics/employeesPerLegalEntity'

const INDEX_BY = 'legalName'
const NO_BOTTOM_AXIS = null
const NO_ELLIPSIS = {}

type Props = {
  employeesPerLegalEntity: EmployeesPerLegalEntity
}

const ellipsize = (maxLength: number) => (value: string) =>
  value.length > maxLength ? `${value.substring(0, maxLength)}...` : value

const EmployeesEmployeesPerLegalEntityChart = ({ employeesPerLegalEntity }: Props) => {
  const numEmployeesInEntities = employeesPerLegalEntity.generate()

  const numOfEmployeesWithoutEntities = numEmployeesInEntities.map(({ count }) => count)
  const totalNumberOfEmployees = _.sum(numOfEmployeesWithoutEntities)
  const max = Math.max(...numOfEmployeesWithoutEntities)
  const maxY = magnitude(max)

  const left = useMarginEffect(maxY)
  const coordinates = useMouseCoords()

  const formatBottomAxis = () => {
    if (numEmployeesInEntities.length > 12) return NO_BOTTOM_AXIS

    if (numEmployeesInEntities.length > 6) {
      return { format: ellipsize(4) }
    }
    if (numEmployeesInEntities.length > 2) {
      return { format: ellipsize(12) }
    }
    if (numEmployeesInEntities.length > 1) {
      return { format: ellipsize(24) }
    }

    return NO_ELLIPSIS
  }

  return (
    <Box height="s300" width="full">
      <BarChart
        margin={{ top: 30, bottom: 25, left, right: 0 }}
        tooltip={({ label, value }) => {
          return (
            <DraggableNode coordinates={coordinates}>
              <ChartTooltip title={label.toString()}>
                <Box gap="s8">
                  <TooltipLegend
                    color={'viridian1000'}
                    name={i18n.t('insights.widgets.shared.headcount')}
                    value={value}
                  />
                  <ChartTooltipPercentage percentage={(value / totalNumberOfEmployees) * 100} />
                </Box>
              </ChartTooltip>
            </DraggableNode>
          )
        }}
        colors={['viridian1000']}
        maxValue={maxY}
        keys={['count']}
        indexBy={INDEX_BY}
        data={numEmployeesInEntities}
        axisBottom={formatBottomAxis()}
      />
    </Box>
  )
}

export default EmployeesEmployeesPerLegalEntityChart
